import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Globe2,
  Bot,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { useAuth } from "@/contexts/AuthContext";
import { onboardingApi, ApiError, type SubdomainSuggestion } from "@/lib/api";

const SUBDOMAIN_RE = /^[a-z][a-z0-9-]{2,62}$/;
const SUBDOMAIN_CHARS_RE = /^[a-z0-9-]+$/;

// Returns the most specific reason a subdomain candidate is invalid, or null
// when the format passes. Mirrors the backend's util.ValidateSubdomainFormat
// rules (length, charset, no leading/trailing hyphen, must start with a letter)
// but surfaces each as its own message so the UI can tell the user precisely
// what to fix.
function explainInvalid(value: string): string | null {
  if (value.length < 3) return "Must be at least 3 characters.";
  if (value.length > 63) return "Must be 63 characters or fewer.";
  if (!/^[a-z]/.test(value)) return "Must start with a lowercase letter.";
  if (!SUBDOMAIN_CHARS_RE.test(value)) {
    return "Use lowercase letters, digits, and hyphens only.";
  }
  if (value.startsWith("-") || value.endsWith("-")) {
    return "Cannot start or end with a hyphen.";
  }
  return null;
}

const schema = z.object({
  subdomain: z
    .string()
    .min(3, "Must be at least 3 characters.")
    .max(63, "Must be 63 characters or fewer.")
    .regex(
      SUBDOMAIN_RE,
      "Use lowercase letters, digits, and hyphens only — must start with a letter.",
    ),
});
type FormValues = z.infer<typeof schema>;

type Status =
  | "idle"
  | "typing"
  | "checking"
  | "available"
  | "unavailable"
  | "reserved"
  | "invalid";

export function StepSubdomain() {
  const { idToken } = useAuth();
  const onboardingId = useOnboardingStore((s) => s.onboardingId);
  const setStep = useOnboardingStore((s) => s.setStep);
  const setSubdomainInStore = useOnboardingStore((s) => s.setSubdomain);
  const setCompany = useOnboardingStore((s) => s.setCompany);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { subdomain: "" },
  });
  const value = form.watch("subdomain");

  const [status, setStatus] = useState<Status>("idle");
  const [reason, setReason] = useState<string | null>(null);

  const [suggestions, setSuggestions] = useState<SubdomainSuggestion[] | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // 300 ms debounced availability check.
  useEffect(() => {
    if (!value) {
      setStatus("idle");
      setReason(null);
      return;
    }
    const reasonText = explainInvalid(value);
    if (reasonText) {
      setStatus("invalid");
      setReason(reasonText);
      return;
    }
    setStatus("typing");
    setReason(null);
    const handle = setTimeout(async () => {
      setStatus("checking");
      try {
        const res = await onboardingApi.checkSubdomain(value);
        if (res.available) {
          setStatus("available");
        } else if (res.reason === "reserved") {
          setStatus("reserved");
          setReason("That subdomain is reserved.");
        } else if (res.reason === "taken") {
          setStatus("unavailable");
          setReason("That subdomain is already taken.");
        } else {
          setStatus("invalid");
          setReason("That subdomain isn't allowed.");
        }
      } catch (err) {
        const ae = err as ApiError;
        if (ae.status === 0 || ae.code === "not_implemented") {
          setStatus("available");
          setReason("Backend check unavailable — proceeding optimistically.");
        } else {
          console.error("checkSubdomain", ae);
          setStatus("invalid");
          setReason(ae.message);
        }
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [value]);

  const loadSuggestions = async () => {
    if (!onboardingId) {
      toast.error("Please restart onboarding");
      return;
    }
    if (!idToken) {
      toast.error("Please sign in again");
      return;
    }
    setLoadingSuggestions(true);
    try {
      const res = await onboardingApi.getSubdomainSuggestions(onboardingId, idToken);
      setSuggestions(res.suggestions.slice(0, 3));
    } catch (err) {
      const ae = err as ApiError;
      console.error("getSubdomainSuggestions", ae);
      if (ae.status === 0 || ae.code === "not_implemented") {
        toast.message("Suggestions unavailable right now");
      } else {
        toast.error("Couldn't load suggestions", { description: ae.message });
      }
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const pickSuggestion = (sub: string) => {
    form.setValue("subdomain", sub, { shouldValidate: true, shouldDirty: true });
  };

  const onSubmit = async (values: FormValues) => {
    if (!onboardingId) {
      toast.error("Please restart onboarding");
      return;
    }
    if (!idToken) {
      toast.error("Please sign in again");
      return;
    }
    try {
      const res = await onboardingApi.setSubdomain(onboardingId, values.subdomain, idToken);
      setSubdomainInStore(res.subdomain);
      setCompany(res.client_id, res.client_code);
      // Wizard terminal: StepComplete polls /dns-status until subdomain_ready.
      setStep("complete");
      if (res.dns_provisioned) {
        toast.success("Subdomain locked in", {
          description: "We're propagating DNS — you'll get an email when it's reachable.",
        });
      } else {
        toast.message("Subdomain saved", {
          description: "DNS provisioning is pending. We'll keep retrying behind the scenes.",
        });
      }
    } catch (err) {
      const ae = err as ApiError;
      if (ae.status === 409) {
        toast.error("Just taken — try another");
      } else {
        console.error("setSubdomain", ae);
        toast.error("Couldn't claim subdomain", { description: ae.message });
      }
    }
  };

  const subError = form.formState.errors.subdomain?.message;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet">
            <Globe2 className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>Pick your subdomain</CardTitle>
            <CardDescription>
              This becomes your workspace URL and ticket prefix.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="subdomain">Subdomain</Label>
            <div className="flex items-center gap-2">
              <Input
                id="subdomain"
                autoFocus
                placeholder="your-company"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                aria-invalid={status !== "available" && !!value}
                aria-describedby={subError || reason ? "subdomain-help" : undefined}
                {...form.register("subdomain")}
              />
              <span className="font-mono text-sm text-muted-foreground whitespace-nowrap">
                .virtualdesk.ai
              </span>
            </div>
            <SubdomainBadge status={status} reason={reason} />
            {subError && (
              <p id="subdomain-help" className="text-xs text-signal-failed">
                {subError}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <button
              type="button"
              onClick={loadSuggestions}
              disabled={loadingSuggestions}
              className="inline-flex items-center gap-1.5 text-sm text-brand-violet hover:text-brand-violet/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded disabled:opacity-60"
            >
              <Bot className={loadingSuggestions ? "h-3.5 w-3.5 animate-pulse" : "h-3.5 w-3.5"} />
              {loadingSuggestions ? "Loading…" : "Show suggestions"}
            </button>
            {suggestions && suggestions.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button
                    type="button"
                    key={s.subdomain}
                    onClick={() => pickSuggestion(s.subdomain)}
                    disabled={!s.available}
                    className={
                      "rounded-full border px-3 py-1 text-xs font-mono transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
                      (s.available
                        ? "border-brand-violet/50 text-brand-violet hover:bg-brand-violet/10"
                        : "border-border text-muted-foreground opacity-60 cursor-not-allowed")
                    }
                  >
                    {s.subdomain}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={status !== "available" || form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            Claim {value ? `${value}.virtualdesk.ai` : "subdomain"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function SubdomainBadge({
  status,
  reason,
}: {
  status: Status;
  reason: string | null;
}) {
  if (status === "idle")
    return <p className="text-xs text-muted-foreground">Type to check availability.</p>;
  if (status === "typing")
    return <p className="text-xs text-muted-foreground">Typing…</p>;
  if (status === "checking")
    return (
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <Loader2 className="h-3 w-3 animate-spin" /> Checking…
      </p>
    );
  if (status === "available")
    return (
      <p className="text-xs text-signal-ok flex items-center gap-1.5">
        <CheckCircle2 className="h-3 w-3" /> Available
      </p>
    );
  return (
    <p className="text-xs text-signal-failed flex items-center gap-1.5">
      <XCircle className="h-3 w-3" /> {reason ?? "Not available"}
    </p>
  );
}
